import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/api";
import { Droplets } from "lucide-react";

const BG_IMAGE =
  "https://images.pexels.com/photos/248395/pexels-photo-248395.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@lavadero.com");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50">
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-8 md:p-10 shadow-sm"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center">
              <Droplets className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight">
                AquaLav
              </div>
              <div className="text-xs text-slate-500">
                Gestión de lavado de autos
              </div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 mb-1">
            Iniciar sesión
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            Accede a tu panel para gestionar ventas y gastos.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full mt-7 bg-blue-600 hover:bg-blue-700 text-white h-11 text-base"
            data-testid="login-submit-button"
          >
            {submitting ? "Entrando…" : "Entrar"}
          </Button>

          <p className="text-xs text-slate-500 mt-6 text-center">
            Por defecto: <code>admin@lavadero.com</code> / <code>admin123</code>
          </p>
        </form>
      </div>

      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/60 via-blue-900/40 to-blue-600/30" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-4xl xl:text-5xl font-bold tracking-tight max-w-md leading-tight">
            Lleva el control total de tu lavadero.
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            Ventas, gastos y ganancia diaria a un clic. Modifica precios y
            extras cuando quieras.
          </p>
        </div>
      </div>
    </div>
  );
}
